/**
 * @fileOverview Both CityPlanEdit and CityPlanView had slightly different
 * implementations of rendering a table of rosters. This pulls the code out
 * into one place.
 */

import React, { useMemo } from 'react'
import { getRosterLink } from 'Utils/Link'
import { ACTION_EDIT } from 'constants.js'
import { Link } from 'react-router-dom'
import { LinkEdit } from 'Components/Link/Edit'
import { LinkView } from 'Components/Link/View'
import { Table } from 'Components/Table'
import { Td } from 'Components/Table/Td'
import { Th } from 'Components/Table/Th'
import { Tr } from 'Components/Table/Tr'
import { getDateOfISOWeek } from 'shared/libraries/utils/date'
import { numberFormatter } from './../../Utils/Formatters'
import { MISSION_STATUS_ENUM } from './../../shared/constants'
import { getWeekNumber } from '../../Utils/Date/index'
import { weekSortBase } from '../../Utils/Table'

const RostersTable = ({ rosters, campaignId, cityPlanId }) => {
  return (
    <div className="negative-margin">
      <Table>
        <thead>
          <Tr>
            <Th># Week</Th>
            <Th>Start Date</Th>
            <Th className="centered">Completed Missions</Th>
            <Th className="centered">Total # Missions</Th>
            <Th>Actions</Th>
          </Tr>
        </thead>
        <tbody>
          {/* Reverse so newest rosters are at the top */}
          {rosters
            .map(roster => {
              var viewUrl = getRosterLink(
                campaignId,
                cityPlanId,
                roster.year + '-' + roster.weekNum,
              )
              if (roster.status === MISSION_STATUS_ENUM.DRAFT)
                var editUrl = getRosterLink(
                  campaignId,
                  cityPlanId,
                  roster.year + '-' + roster.weekNum,
                  ACTION_EDIT,
                )
              return (
                <RostersTableRow
                  roster={roster}
                  viewUrl={viewUrl}
                  editUrl={editUrl}
                />
              )
            })
            .sort(weekSortBase)
            .reverse()}
        </tbody>
      </Table>
    </div>
  )
}
export default RostersTable

/**
 * Render rosters table row
 *
 * @param roster
 * @param index
 * @returns {*}
 */
const RostersTableRow = ({ roster, viewUrl, editUrl }) => {
  const WrapContent = ({ children }) => (
    <Link className="invisible-link" to={viewUrl}>
      {children}
    </Link>
  )

  const startDate = getDateOfISOWeek(roster.weekNum, roster.year)

  const completedMissions = roster.missions.filter(
    mission =>
      [MISSION_STATUS_ENUM.COMPLETED, MISSION_STATUS_ENUM.PAID].includes(
        mission.status,
      ) && !mission.cancelled,
  )

  const highlightClass = useMemo(() => {
    const date = new Date()
    const currentWeek = getWeekNumber(date)
    const currentYear = date.getFullYear()

    return Number(roster.weekNum) === currentWeek &&
      Number(roster.year) === currentYear
      ? `active`
      : ''
  }, [roster.year, roster.weekNum])

  return (
    <Tr className={highlightClass} key={`rosters-row-${roster.weekNum}`}>
      <Td>
        <WrapContent>#{roster.weekNum}</WrapContent>
      </Td>
      <Td>
        <WrapContent>{startDate.toLocaleDateString()}</WrapContent>
      </Td>
      <Td className="centered">
        <WrapContent>{numberFormatter(completedMissions.length)}</WrapContent>
      </Td>
      <Td className="centered">
        <WrapContent>{numberFormatter(roster.missions.length)}</WrapContent>
      </Td>
      <Td actions className="no-opacity centered">
        <LinkView className="link-view" to={viewUrl} />
        {editUrl && <LinkEdit className="link-edit" to={editUrl} />}
      </Td>
    </Tr>
  )
}



// WEBPACK FOOTER //
// ./src/Components/RostersTable/index.js